'use client'

import { useEffect, useRef, useState } from 'react'

export type SubmitState = 'idle' | 'submitting' | 'success' | 'error'

/**
 * Socle d'envoi commun aux trois formulaires.
 *
 * Le navigateur poste vers un Route Handler de Next (le BFF), jamais vers
 * l'API directement : aucun secret côté client, pas de CORS, et l'IP réelle
 * est transmise au backend (docs/architecture.md § 1.1).
 */
export function useFormSubmit(endpoint: string) {
  const [state, setState] = useState<SubmitState>('idle')
  // Horodatage d'ouverture : un envoi trop rapide est probablement un robot.
  // Posé après le montage, jamais pendant le rendu (fonction impure).
  const startedAt = useRef(0)

  useEffect(() => {
    startedAt.current = Date.now()
  }, [])

  async function submit(payload: Record<string, unknown> | FormData) {
    setState('submitting')

    try {
      const isFormData = payload instanceof FormData
      if (isFormData) {
        payload.set('startedAt', String(startedAt.current))
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        ...(isFormData
          ? { body: payload }
          : {
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ ...payload, startedAt: startedAt.current }),
            }),
      })

      if (!response.ok) {
        setState('error')
        return false
      }

      setState('success')
      return true
    } catch {
      setState('error')
      return false
    }
  }

  function reset() {
    startedAt.current = Date.now()
    setState('idle')
  }

  return { state, submit, reset, startedAt }
}
